/**
 * HelpModal - Reusable modal for feature explanations
 * 
 * Triggered by (?) icons next to feature buttons on the home page.
 * Provides context about each feature before the user engages.
 */

import React from 'react';
import { X, Calendar, BookOpen, Music } from 'lucide-react';
import { glassStyles } from '@/lib/design';
import { useTranslation } from '@/lib/i18n';

// ============================================================================
// Types
// ============================================================================

export type HelpTopic = 'schedule' | 'consonant-practice' | 'read-foundations';

interface HelpModalProps {
  topic: HelpTopic;
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// Content Configuration
// ============================================================================

interface HelpContent {
  icon: React.ReactNode;
  title: string;
  description: string;
  bullets: string[];
  ctaText?: string;
}

function getHelpContent(t: (key: string) => string): Record<HelpTopic, HelpContent> {
  return {
    'schedule': {
      icon: <Calendar className="w-8 h-8 text-[#0A84FF]" />,
      title: t('Schedule a Lesson'),
      description: t('One-on-one video lessons with a Turn literacy specialist.'),
      bullets: [
        t('30-minute live video sessions'),
        t('Personalized instruction for your child'),
        t('Join from any device with a camera'),
        t('Lessons are scheduled at times that work for you'),
      ],
      ctaText: t('Ready to schedule your first lesson?'),
    },
    'consonant-practice': {
      icon: <Music className="w-8 h-8 text-[#30D158]" />,
      title: t('Cards'),
      description: t('Practice letter sounds using physical cards and your camera.'),
      bullets: [
        t('Use the physical letter cards from your Turn kit'),
        t('Scan the QR code on each card to see it on screen'),
        t('Parent or guardian participation is needed'),
        t('Sit together at a table with cards displayed letters-up'),
      ],
      ctaText: t("Have your cards ready? Let's practice!"),
    },
    'read-foundations': {
      icon: <BookOpen className="w-8 h-8 text-[#FF9F0A]" />,
      title: t('Read with Foundations'),
      description: t('Scan physical books and read with Turn Color phonics support.'),
      bullets: [
        t('Use your camera to scan any physical book'),
        t('Turn Color highlights help children decode words'),
        t('Audio support for challenging words'),
        t("Works with any children's book you own"),
      ],
      ctaText: t("Grab a book and let's read!"),
    },
  };
}

// ============================================================================
// Component
// ============================================================================

export const HelpModal: React.FC<HelpModalProps> = ({ topic, isOpen, onClose }) => {
  const { t } = useTranslation();
  
  if (!isOpen) return null;
  
  const content = getHelpContent(t)[topic];
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="relative w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, rgba(30,30,30,0.98), rgba(15,15,15,0.98))',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Content */}
        <div className="p-6">
          {/* Icon */}
          <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
            {content.icon}
          </div>
          
          {/* Title & Description */}
          <h2 className="text-xl font-semibold text-white mb-2">
            {content.title}
          </h2>
          <p className="text-white/60 mb-6">
            {content.description}
          </p>
          
          {/* Bullet points */}
          <ul className="space-y-3 mb-6">
            {content.bullets.map((bullet, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#0A84FF] mt-2 flex-shrink-0" />
                <span className="text-white/80 text-sm">{bullet}</span>
              </li>
            ))}
          </ul>
          
          {/* CTA */}
          {content.ctaText && (
            <p className="text-sm text-white/40 text-center italic">
              {content.ctaText}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
